import moment from "moment";
import "moment/locale/vi";
import {
  APPLY_TO_LEVEL_OPTIONS,
  APPLY_TO_PRODUCT_TYPES,
  ATTRACTION_QR_CODE_SENDING_STATUS,
  CONTENT_ORDER_PAYMENT_METHODS,
  CURRENCY_LIST,
  DISCOUNT_TYPES,
  EVENT_ADDON_QUANTITY_MODES,
  EVENT_ADDON_SCOPES,
  EVENT_ADDON_TYPES,
  ORDER_STATUS,
  ORDER_TYPES,
  PAYMENT_METHOD_FREE,
  PAYMENT_METHODS,
  PAYMENT_STATUS_LIST,
  SELF_PICK_STATUS,
  TICKET_PLATFORMS,
} from "./constants";
import {
  AttractionQRCodeSendingStatus,
  IAttendantTemplateInputItem,
  InputType,
  OrderStatusKey,
  PaymentStatusType,
  SupportedLanguage,
  UserType,
} from "interfaces";
import validator from "validator";
import { get } from "lodash";
import toast from "react-hot-toast";
import { PermissionType } from "components/wps/sidebarWPS";

export function isInvalidImageSize(file: File, maximumSize: number) {
  if (file.size > maximumSize) {
    // bytes
    return true;
  }
  return null;
}

export function bytesForHuman(bytes: number) {
  let units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let i = 0;
  for (i; bytes > 1024; i++) {
    bytes /= 1024;
  }
  return bytes.toFixed(1) + " " + units[i];
}

export const arrayCompare = (_arr1: Array<any>, _arr2: Array<any>) => {
  if ([..._arr1].sort().join(",") === [..._arr2].sort().join(",")) {
    return true;
  }
  return false;
};

export const abbreviateNumbers = (number: number) => {
  return Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(number);
};

export const formatNumber = (num: number) => {
  const rounded = Math.round(num * 100) / 100;

  const hasDecimal = rounded % 1 !== 0;

  if (hasDecimal) {
    return rounded.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    // return integer
    return rounded.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
};

export const isObjectEmpty = (object: Object) =>
  Object.values(object).every((x) => x === null || x === "");

export const _generateSlug = (text: string | undefined) => {
  return text
    ? toLowerCaseNonAccentVietnamese(text)
        .toLowerCase()
        .replace(/[^\w ]+/g, "")
        .replace(/ +/g, "-")
    : "";
};

function toLowerCaseNonAccentVietnamese(str: string) {
  str = str.toLowerCase();
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
  str = str.replace(/đ/g, "d");
  // Some system encode vietnamese combining accent as individual utf-8 characters
  str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // Huyền sắc hỏi ngã nặng
  str = str.replace(/\u02C6|\u0306|\u031B/g, ""); // Â, Ê, Ă, Ơ, Ư
  return str;
}

export const isHTML = RegExp.prototype.test.bind(/(<([^>]+)>)/i);

export const getEventDate = (
  start: string,
  end: string,
  lang: string = "en"
) => {
  let date = "";
  const start_date = moment(start);
  const end_date = moment(end);
  start_date.locale(lang);
  end_date.locale(lang);

  const is_same_day = start_date.isSame(end_date, "day");

  if (is_same_day) {
    const dayOfWeek = start_date.format("dddd");
    const day = start_date.format("DD");
    const month = start_date.format("MMMM");
    const year = start_date.format("YYYY");

    date =
      lang === "vi"
        ? `${dayOfWeek}, ${day} ${month}, ${year}`
        : `${dayOfWeek}, ${month} ${day}, ${year}`;
  } else {
    const day_start = start_date.format("DD");
    const month_start = start_date.format("MMMM");
    const year_start = start_date.format("YYYY");

    const day_end = end_date.format("DD");
    const month_end = end_date.format("MMMM");
    const year_end = end_date.format("YYYY");

    if (year_start !== year_end) {
      date = `${day_start} ${month_start}, ${year_start} - ${day_end} ${month_end}, ${year_end}`;
    } else {
      if (month_start !== month_end) {
        date = `${day_start} ${month_start} - ${day_end} ${month_end}, ${year_start}`;
      } else {
        date = `${day_start} - ${day_end} ${month_start}, ${year_start}`;
      }
    }
  }

  return date;
};

export const paginate = (
  array: any[],
  page_number: number,
  pageSize: number = 15
) => {
  return array.slice((page_number - 1) * pageSize, page_number * pageSize);
};

export const getPaymentMethodLabel = (method: string) => {
  return (
    (CONTENT_ORDER_PAYMENT_METHODS as any[])
      .concat(PAYMENT_METHOD_FREE)
      .find((paymentMethod) => paymentMethod.value === method)?.label ?? method
  );
};

export const getOrderStatusLabel = (status: string) => {
  return (
    ORDER_STATUS.find((orderStatus) => orderStatus.value === status)?.label ??
    status
  );
};

export const getOrderTypeLabel = (type: string) => {
  return (
    ORDER_TYPES.find((orderType) => orderType.value === type)?.label ?? type
  );
};

export const getEventAddonTypeLabel = (type: string) => {
  return (
    EVENT_ADDON_TYPES.find((addonType) => addonType.value === type)?.label ??
    type
  );
};

export const getEventAddonScopeLabel = (scope: string) => {
  return (
    EVENT_ADDON_SCOPES.find((addonScope) => addonScope.value === scope)
      ?.label ?? scope
  );
};

export const getEventAddonQuantityModeLabel = (quantityMode: string) => {
  return (
    EVENT_ADDON_QUANTITY_MODES.find(
      (addonQuantityMode) => addonQuantityMode.value === quantityMode
    )?.label ?? quantityMode
  );
};

export const getPaymentStatusLabel = (status: number) => {
  return (
    PAYMENT_STATUS_LIST.find((paymentStatus) => paymentStatus.value === status)
      ?.label ?? status
  );
};

export const getTicketPlatformLabel = (platform: string) => {
  return (
    TICKET_PLATFORMS.find((ticketPlatform) => ticketPlatform.value === platform)
      ?.label ?? platform
  );
};

export const getSelfPickupStatus = (status: any) => {
  return (
    SELF_PICK_STATUS.find((selfPickStatus) => selfPickStatus.value === status)
      ?.label ?? status
  );
};

export const getApplyToProductTypeLabel = (productType: any) => {
  return APPLY_TO_PRODUCT_TYPES.find(type => type.value === productType)?.label ?? productType
}

export const getDiscountTypeLabel = (discountType: any) => {
  return DISCOUNT_TYPES.find(type => type.value === discountType)?.label ?? discountType
}


export const getApplyToLevelLabel = (applyToLevel: any) => {
  return (
    APPLY_TO_LEVEL_OPTIONS.find((option) => option.value === applyToLevel)
      ?.label ?? applyToLevel
  );
};

export const getPaymentStatusTypeByOrderStatus = (
  orderStatus: OrderStatusKey
) => {
  switch (orderStatus) {
    case OrderStatusKey.SUCCESSFUL:
      return PaymentStatusType.SUCCESS;
    case OrderStatusKey.FAILED:
      return PaymentStatusType.FAIL;
    case OrderStatusKey.PENDING:
      return PaymentStatusType.REVIEW;
    case OrderStatusKey.REFUNDED:
      return PaymentStatusType.REFUND;
    case OrderStatusKey.WAITING_FOR_REFUND:
      return PaymentStatusType.REFUND_REVIEW;
    default:
      return -1;
  }
};

export const getAttractionQRCodeSendingStatus = (
  status: AttractionQRCodeSendingStatus
) => {
  return (
    ATTRACTION_QR_CODE_SENDING_STATUS.find(
      (sendingStatus) => sendingStatus.value === status
    )?.label ?? status
  );
};

export const _openUrlOnNewTab = (url: string) => {
  setTimeout(() => {
    window.open(url, "_blank");
  });
};

export const getPixFileName = (url: string) => {
  return url.split("/").pop();
};

export const isOpenRegistration = (openRegistrationDate?: string) => {
  return openRegistrationDate
    ? moment().utc().isAfter(moment(openRegistrationDate).utc())
    : false;
};

export const currencySymbol = (selectedCurrency?: string) => {
  return CURRENCY_LIST.find(
    (currency) =>
      currency.code.toLowerCase() ===
      (
        selectedCurrency ??
        localStorage.getItem("currency") ??
        "VND"
      ).toLowerCase()
  )?.symbol;
};

export const getDefaultEventLanguageValue = (
  languages: SupportedLanguage[]
) => {
  const value = {} as any;
  languages.map((lang) => (value[lang] = ""));
  return value;
};

export const isValidURL = (url: string) => {
  return validator.isURL(url, {
    protocols: ["http", "https", "ftp"],
    require_protocol: true,
  });
};

export const calculateAge = (
  birthDate: string | Date,
  referenceDate: string | Date = new Date()
): number => {
  // Chuyển đổi chuỗi thành đối tượng Date nếu cần
  const birthDateObj =
    typeof birthDate === "string" ? new Date(birthDate) : birthDate;
  const referenceDateObj =
    typeof referenceDate === "string" ? new Date(referenceDate) : referenceDate;

  // Tính toán tuổi
  let age = referenceDateObj.getFullYear() - birthDateObj.getFullYear();
  const monthDiff = referenceDateObj.getMonth() - birthDateObj.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && referenceDateObj.getDate() < birthDateObj.getDate())
  ) {
    age--;
  }

  return age;
};

export const getInputFieldValue = (
  field: IAttendantTemplateInputItem,
  value: any
) => {
  if (!String(value)) return "N/A";
  switch (field.type) {
    case InputType.CHECKBOX:
      return (value === "1" || value === true) ? "True" : "False";
    case InputType.SELECT: {
      const optionArr = Object.keys(field.option ?? {}).map((key) => ({
        value: key ?? "",
        label: get(field.option[key], "label_en", "") || "",
      }));
      return optionArr.find(
        (opt: { label: string; value: string }) => opt.value === value
      )?.label;
    }
    case InputType.RADIO_BUTTON: {
      const optionArr = (field.option ?? []).map(
        (opt: { Key: string; Value: any }[]) => ({
          value: get(opt[0], "Value", ""),
          label: get(opt[1], "Value[0].Value", ""),
        })
      );
      return optionArr.find(
        (opt: { label: string; value: string }) => opt.value === value
      )?.label;
    }
    case InputType.DATE:
      return moment(value).format("DD/MM/YYYY");
    case InputType.DATETIME:
      return moment(value).format("DD/MM/YYYY HH:mm:ss");
    default:
      return value;
  }
};

export const formatPhoneNumber = (phone: string, phoneAreaCode: string) => {
  return phone ? `${phoneAreaCode ? `(+${phoneAreaCode})` : ""} ${phone}` : "";
};

export const generateUUID = () => {
  let uuid = "",
    i,
    random;

  for (i = 0; i < 32; i++) {
    random = (Math.random() * 16) | 0;

    if (i === 12) {
      random = 4; // UUID version 4
    } else if (i === 16) {
      random = (random & 0x3) | 0x8; // variant
    }

    uuid +=
      (i === 8 || i === 12 || i === 16 || i === 20 ? "-" : "") +
      random.toString(16);
  }
  return uuid;
};

export const getFormattedDate = (date: string) => {
  if (!date) return "";
  return moment(date).format("DD/MM/YYYY");
};

export const toastMessage = (message: string) => {
  const key = "shown_toast_messages";

  // Lấy danh sách message đã toast từ localStorage
  const raw = localStorage.getItem(key);
  const shownMessages: string[] = raw ? JSON.parse(raw) : [];

  // Nếu message đã tồn tại thì không toast nữa
  if (shownMessages.includes(message)) return;

  // Toast và lưu vào danh sách
  
  toast.error(message, {
    id: message ?? "",
  });
  shownMessages.push(message);
  localStorage.setItem(key, JSON.stringify(shownMessages));
};

export const resetToastMessages = () => {
  localStorage.removeItem("shown_toast_messages");
};
export const getFormattedDateTime = (datetime: string) => {
  if (!datetime) return "";
  return moment(datetime).format("DD/MM/YYYY HH:mm");
};

export const canAccessByType = (requiredType: UserType) => {
  try {
    const user = JSON.parse(localStorage.getItem("USER") ?? "{}");
    const userType: UserType = user?.type;

    if (!userType) {
      return false;
    }

    return userType.toLowerCase() === requiredType.toLowerCase();
  } catch (error) {
    console.error("Error checking type:", error);
    return false;
  }
};

export const isHaveActionPermission = (
  id: string,
  parrentId: string,
  action: "view" | "create" | "edit" | "export" | "delete",
  permissons?: null | PermissionType[]
) => {
  if (!permissons) {
    const user = JSON.parse(localStorage.getItem("USER") ?? "{}");
    if (user && user?.permissions) {
      const isValid = (user?.permissions ?? [])?.find((p: any) => {
        return (
          p?.resource === id &&
          p?.app === parrentId &&
          p?.action?.includes(action)
        );
      });
      if (!isValid) return false;
      return true;
    }
    return false;
  }

  const p = permissons?.find((p: PermissionType) => {
    return (
      p?.id === id &&
      p?.parrent === parrentId &&
      p?.permissions?.includes(action)
    );
  });

  // console.log(`Is have ${id} ${action} permission:`, p, permissons);

  if (!p) return false;

  return true;
};

// Hàm này trả về mảng tất cả các permission: ['dashboard', 'orders', 'attendees', 'organization', 'coupon', 'event']
export const getPermissionsUniqueParrents = (
  data: PermissionType[]
): string[] => {
  if (!data) return [];

  const parrentSet = new Set<string>();
  data.forEach((item) => parrentSet.add(item.parrent));

  return Array.from(parrentSet);
};

export const capitalizeFirstLetter = (val: string) => {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
};

export const _generateTemplateKey = (text: string | undefined) => {
  if (!text || typeof text !== "string") {
    return "";
  }
  return (
    "custom_" +
    toLowerCaseNonAccentVietnamese(text)
      .replace(/[^a-zA-Z0-9\s_]/g, "")
      .replace(/^[\s_]+|[\s_]+$/g, "")
      .replace(/\s+/g, " ")
      .replace(/\s/g, "_")
      .replace(/_+/g, "_")
      .toLowerCase()
  ).substring(0, 50);
};

export const convertCurrency = (
  productPrice: number,
  productCurrency: string,
  rate: number
) => {
  const targetCurrency = (
    canAccessByType(UserType.ADMIN)
      ? localStorage.getItem("currency")
      : localStorage.getItem("wsCurrency")
  )?.toLocaleLowerCase();
  const finalTargetCurrency = (
    targetCurrency || productCurrency
  ).toLocaleLowerCase();
  
  const finalProductCurrency = productCurrency.toLocaleLowerCase();
  // No convert
  if (finalProductCurrency === finalTargetCurrency) {
    return formatNumber(Number(productPrice));
  }

  let convertedPrice: number;
  let exchangeRate: number;

  // VND to USD
  if (finalProductCurrency === "vnd" && finalTargetCurrency === "usd") {
    convertedPrice = Number(productPrice) / rate;
    exchangeRate = 1 / rate;
  }
  // USD to VND
  else if (finalProductCurrency === "usd" && finalTargetCurrency === "vnd") {
    convertedPrice = Math.round(Number(productPrice) * rate);
    exchangeRate = rate;
  } else {
    convertedPrice = Number(productPrice);
    exchangeRate = 1;
  }
  return formatNumber(Math.round(convertedPrice * 100) / 100);
};

export const sanitizeHtml =(input: string): string => {
  if (typeof input !== "string") return "";

  return input
    // Null bytes
    .replace(/\x00/g, "")

    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "")
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, "")
    .replace(/<embed\b[^>]*\/?>/gi, "")
    .replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, "")

    // HTML comments
    .replace(/<!--[\s\S]*?-->/g, "")

    // Event handlers (on*)
    .replace(/\s+on\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\s+on\w+\s*=\s*'[^']*'/gi, "")
    .replace(/\s+on\w+\s*=\s*[^\s>]+/gi, "")

    // javascript: href, src, action
    .replace(/(href|src|action)\s*=\s*["']\s*javascript\s*:[^"']*["']/gi, '$1="#"')

    // style contain javascript hoặc expression
    .replace(/style\s*=\s*["'][^"']*(?:expression\s*\(|javascript\s*:)[^"']*["']/gi, "")

    .trim();
}
